import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Spin,
  message,
  Divider
} from 'antd';
import {
  DownloadOutlined,
  PrinterOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface CashFlowLineItem {
  description: string;
  amount: number;
  accountId?: string;
}

interface CashFlowData {
  fromDate: string;
  toDate: string;
  operating: {
    items: CashFlowLineItem[];
    netCash: number;
  };
  investing: {
    items: CashFlowLineItem[];
    netCash: number;
  };
  financing: {
    items: CashFlowLineItem[];
    netCash: number;
  };
  netChangeInCash: number;
  beginningCash: number;
  endingCash: number;
}

const formatCurrency = (amount: number) => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(amount);
};

const CashFlowReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CashFlowData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('year'),
    dayjs()
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        fromDate: dateRange[0].format('YYYY-MM-DD'),
        toDate: dateRange[1].format('YYYY-MM-DD')
      });

      const response = await apiService.get(`/accounting/reports/cash-flow?${params}`);
      setData(response.data || response);
    } catch (error: any) {
      message.error(error.message || 'Failed to load cash flow statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns: ColumnsType<CashFlowLineItem> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 400
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (val: number) => {
        const color = val > 0 ? '#52c41a' : val < 0 ? '#f5222d' : undefined;
        return <Text style={{ color }}>{formatCurrency(val)}</Text>;
      }
    }
  ];

  const handleDateChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleExport = () => {
    if (!data) {
      message.warning('No data to export');
      return;
    }
    const rows: Record<string, any>[] = [];
    const addSection = (title: string, items: CashFlowLineItem[], netCash: number) => {
      rows.push({ 'Section': title, 'Description': '', 'Amount': '' });
      items.forEach(item => {
        rows.push({ 'Section': '', 'Description': item.description, 'Amount': Number(item.amount || 0) });
      });
      rows.push({ 'Section': '', 'Description': `Net Cash - ${title}`, 'Amount': Number(netCash || 0) });
      rows.push({ 'Section': '', 'Description': '', 'Amount': '' });
    };
    addSection('Operating Activities', data.operating.items, data.operating.netCash);
    addSection('Investing Activities', data.investing.items, data.investing.netCash);
    addSection('Financing Activities', data.financing.items, data.financing.netCash);
    rows.push({ 'Section': '', 'Description': 'Net Change in Cash', 'Amount': Number(data.netChangeInCash || 0) });
    rows.push({ 'Section': '', 'Description': 'Beginning Cash', 'Amount': Number(data.beginningCash || 0) });
    rows.push({ 'Section': '', 'Description': 'Ending Cash', 'Amount': Number(data.endingCash || 0) });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 35 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');
    XLSX.writeFile(wb, `Cash_Flow_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('Cash Flow Statement exported');
  };

  const handlePrint = () => {
    window.print();
  };

  const renderCashFlowSection = (
    title: string,
    items: CashFlowLineItem[],
    netCash: number,
    bgColor: string,
    textColor: string
  ) => (
    <>
      <Title level={5} style={{ color: textColor, marginBottom: 16 }}>{title}</Title>
      <Table
        columns={columns}
        dataSource={items}
        rowKey={(record, index) => `${record.description}-${index}`}
        pagination={false}
        bordered
        size="small"
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row style={{ backgroundColor: bgColor }}>
              <Table.Summary.Cell index={0}>
                <Text strong>Net Cash from {title}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong style={{ color: netCash >= 0 ? '#52c41a' : '#f5222d' }}>
                  {formatCurrency(netCash)}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </>
  );

  const getCashFlowIndicator = () => {
    if (!data) return null;
    if (data.netChangeInCash > 0) {
      return <Tag icon={<ArrowUpOutlined />} color="success">Cash Increased</Tag>;
    } else if (data.netChangeInCash < 0) {
      return <Tag icon={<ArrowDownOutlined />} color="error">Cash Decreased</Tag>;
    }
    return <Tag>No Change</Tag>;
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Cash Flow Statement</Title>
          {getCashFlowIndicator()}
        </Space>
      }
      extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            {/* Summary Statistics */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Beginning Cash"
                  value={data.beginningCash}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Net Change in Cash"
                  value={data.netChangeInCash}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: data.netChangeInCash >= 0 ? '#52c41a' : '#f5222d' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Ending Cash"
                  value={data.endingCash}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Cash Flow Verification"
                  value={Math.abs(data.beginningCash + data.netChangeInCash - data.endingCash) < 0.01 ? 'Balanced' : 'Variance Detected'}
                  formatter={(value) => (
                    <Tag color={value === 'Balanced' ? 'success' : 'warning'}>{value}</Tag>
                  )}
                />
              </Col>
            </Row>

            <Divider />

            {/* Operating Activities */}
            {renderCashFlowSection(
              'Operating Activities',
              data.operating.items,
              data.operating.netCash,
              '#e6f7ff',
              '#1890ff'
            )}

            <div style={{ marginBottom: 24 }} />

            {/* Investing Activities */}
            {renderCashFlowSection(
              'Investing Activities',
              data.investing.items.length > 0 ? data.investing.items : [{ description: 'No investing activities', amount: 0 }],
              data.investing.netCash,
              '#fff7e6',
              '#fa8c16'
            )}

            <div style={{ marginBottom: 24 }} />

            {/* Financing Activities */}
            {renderCashFlowSection(
              'Financing Activities',
              data.financing.items.length > 0 ? data.financing.items : [{ description: 'No financing activities', amount: 0 }],
              data.financing.netCash,
              '#f9f0ff',
              '#722ed1'
            )}

            <Divider />

            {/* Cash Summary */}
            <Card style={{ backgroundColor: '#f6ffed', marginTop: 16 }}>
              <Row gutter={24}>
                <Col span={8}>
                  <Row justify="space-between" align="middle" style={{ padding: '8px 0' }}>
                    <Text>Beginning Cash Balance</Text>
                    <Text strong>{formatCurrency(data.beginningCash)}</Text>
                  </Row>
                </Col>
                <Col span={8}>
                  <Row justify="space-between" align="middle" style={{ padding: '8px 0' }}>
                    <Text>Net Change in Cash</Text>
                    <Text strong style={{ color: data.netChangeInCash >= 0 ? '#52c41a' : '#f5222d' }}>
                      {data.netChangeInCash >= 0 ? '+' : ''}{formatCurrency(data.netChangeInCash)}
                    </Text>
                  </Row>
                </Col>
                <Col span={8}>
                  <Row justify="space-between" align="middle" style={{ padding: '8px 0' }}>
                    <Text strong>Ending Cash Balance</Text>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                      {formatCurrency(data.endingCash)}
                    </Title>
                  </Row>
                </Col>
              </Row>
            </Card>

            {/* Breakdown by Activity Type */}
            <Row gutter={16} style={{ marginTop: 24 }}>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Operating"
                    value={data.operating.netCash}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: data.operating.netCash >= 0 ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Investing"
                    value={data.investing.netCash}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: data.investing.netCash >= 0 ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Financing"
                    value={data.financing.netCash}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: data.financing.netCash >= 0 ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Text type="secondary">
                Period: {dayjs(data.fromDate).format('MMM DD, YYYY')} to {dayjs(data.toDate).format('MMM DD, YYYY')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cash Flow Statement prepared using the Indirect Method
              </Text>
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
};

export default CashFlowReport;